import { useRef, useState } from "react";
import { Trash2, Upload } from "lucide-react";
import Avatar from "../../../_components/ui/Avatar";
import Button from "../Button";
import ConfirmDialog from "../../modal/ConfirmDialog";
import { useToast } from "../../../_context/ToastContext/ToastContext";
import "./_avatar-upload.scss";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const VALID_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml", "image/gif"];

/**
 * Circular image preview + upload + remove-with-confirmation. Wraps the
 * existing `components/common/Avatar` for the preview circle (an
 * established, already-shared old-tree dependency - not rebuilt here) and
 * adds the controls around it. Validates file size/type itself and hands
 * the caller a raw `File` via `onSelectFile` - it doesn't decide whether
 * upload happens immediately or is deferred to a later "Save" action, that's
 * the caller's call.
 */
const AvatarUpload = ({
  src,
  id,
  name,
  onSelectFile,
  onRemove,
  disabled = false,
  isUploading = false,
  isDeleting = false,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const fileInputRef = useRef(null);
  const { showToast } = useToast();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;

    if (!VALID_TYPES.includes(file.type)) {
      showToast("Invalid file type. Please upload a PNG, JPG, SVG, or GIF image.", "error");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      showToast("Image file is too large. Maximum size is 2MB.", "error");
      return;
    }

    onSelectFile(file);
  };

  const handleConfirmRemove = () => {
    setShowConfirm(false);
    onRemove();
  };

  return (
    <div className="avatar-upload">
      <div className="avatar-upload__preview">
        <Avatar src={src} alt={name} id={id} orgName={name} width="64px" height="64px" borderRadius="12px" />
      </div>

      <div className="avatar-upload__controls">
        <Button
          type="button"
          variant="outlined"
          size="sm"
          icon={<Upload size={14} />}
          disabled={disabled || isUploading}
          loading={isUploading}
          onClick={() => fileInputRef.current?.click()}
        >
          Change photo
        </Button>
        {src && (
          <Button
            type="button"
            variant="ghost-danger"
            size="sm"
            iconOnly
            icon={<Trash2 size={14} />}
            disabled={disabled || isDeleting}
            loading={isDeleting}
            onClick={() => setShowConfirm(true)}
            aria-label="Remove photo"
          />
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="avatar-upload__file-input"
          onChange={handleFileChange}
          disabled={disabled}
        />
      </div>

      <ConfirmDialog
        isOpen={showConfirm}
        title="Remove this photo?"
        description="This will remove the current photo. You can upload a new one anytime."
        confirmText="Yes, remove"
        cancelText="Cancel"
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirmRemove}
      />
    </div>
  );
};

export default AvatarUpload;
